import React, { useState, useEffect } from 'react'
import { formatDate } from '../utils/dateFormat'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

interface Warehouse {
  id: string
  name: string
  isDefault: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  documentsCount?: number
}

const Warehouses: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null)
  const [docsModalWarehouse, setDocsModalWarehouse] = useState<Warehouse | null>(null)
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsData, setDocsData] = useState<{ purchases: any[]; consumptions: any[] } | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    isDefault: false,
    isActive: true
  })

  const fetchWarehouses = async () => {
    try {
      const data = await apiGet<Warehouse[] | { warehouses?: Warehouse[] }>('/warehouses')
      setWarehouses(Array.isArray(data) ? data : (data.warehouses || []))
    } catch (error) {
      console.error('Failed to fetch warehouses:', error)
    } finally {
      setLoading(false)
    }
  }
  const openDocsModal = async (warehouse: Warehouse) => {
    setDocsModalWarehouse(warehouse)
    setDocsLoading(true)
    try {
      const data = await apiGet<{ purchases?: any[]; consumptions?: any[] }>(`/warehouses/${warehouse.id}/documents`)
      setDocsData({ purchases: data.purchases || [], consumptions: data.consumptions || [] })
    } catch (e) {
      console.error('Failed to load warehouse documents', e)
      setDocsData({ purchases: [], consumptions: [] })
    } finally {
      setDocsLoading(false)
    }
  }

  useEffect(() => {
    fetchWarehouses()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingWarehouse) {
        await apiPut(`/warehouses/${editingWarehouse.id}`, formData)
      } else {
        await apiPost('/warehouses', formData)
      }
      
      await fetchWarehouses()
      setShowForm(false)
      setEditingWarehouse(null)
      setFormData({ name: '', isDefault: false, isActive: true })
    } catch (error: any) {
      console.error('Failed to save warehouse:', error)
      alert(error.message || 'Ошибка при сохранении склада')
    }
  }

  const handleEdit = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse)
    setFormData({
      name: warehouse.name,
      isDefault: warehouse.isDefault,
      isActive: warehouse.isActive
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот склад?')) return

    try {
      await apiDelete(`/warehouses/${id}`)
      await fetchWarehouses()
    } catch (error: any) {
      console.error('Failed to delete warehouse:', error)
      alert(error.message || 'Ошибка при удалении склада')
    }
  }

  const handleActivate = async (id: string) => {
    try {
      await apiPut(`/warehouses/${id}`, { isActive: true })
      await fetchWarehouses()
    } catch (error: any) {
      console.error('Failed to activate warehouse:', error)
      alert(error.message || 'Ошибка при активации склада')
    }
  }

  const resetForm = () => {
    setFormData({ name: '', isDefault: false, isActive: true })
    setEditingWarehouse(null)
    setShowForm(false)
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Загрузка складов...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Склады</h1>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          + Добавить склад
        </button>
      </div>

      {/* Форма создания/редактирования */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded shadow p-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingWarehouse ? 'Редактировать склад' : 'Новый склад'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название склада
                </label>
                <input
                  type="text"
                  required
                  className="w-full border rounded px-3 py-2"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  className="mr-2"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                />
                <label htmlFor="isDefault" className="text-sm text-gray-700">
                  Склад по умолчанию
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  className="mr-2"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Активен
                </label>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingWarehouse ? 'Сохранить' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Список складов */}
      <div className="bg-white rounded shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  По умолчанию
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Документы
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {warehouses.map((warehouse) => (
                <tr key={warehouse.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {warehouse.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      warehouse.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {warehouse.isActive ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {warehouse.isDefault ? 'Да' : 'Нет'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {typeof warehouse.documentsCount === 'number' ? (
                      <button
                        onClick={() => openDocsModal(warehouse)}
                        className="text-blue-600 hover:text-blue-800 underline"
                        title="Открыть детализацию документов"
                      >
                        {warehouse.documentsCount}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(warehouse)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Изменить
                    </button>
                    {/* Показываем кнопку удаления только если нет документов */}
                    {(!warehouse.documentsCount || warehouse.documentsCount === 0) && (
                      <button
                        onClick={() => handleDelete(warehouse.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Удалить
                      </button>
                    )}
                    {!warehouse.isActive && (
                      <button
                        onClick={() => handleActivate(warehouse.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Активировать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {warehouses.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Склады не найдены
          </div>
        )}
      </div>

      {docsModalWarehouse && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl rounded shadow p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Документы по складу: {docsModalWarehouse.name}</h2>
              <button onClick={() => { setDocsModalWarehouse(null); setDocsData(null) }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            {docsLoading && <div className="text-gray-500">Загрузка...</div>}

            {!docsLoading && docsData && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium mb-2">Закупки ({docsData.purchases.length})</h3>
                  {docsData.purchases.length === 0 ? (
                    <div className="text-sm text-gray-500">Нет документов</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Номер</th>
                          <th className="px-3 py-2 text-left">Дата</th>
                          <th className="px-3 py-2 text-left">Поставщик</th>
                          <th className="px-3 py-2 text-right">Сумма</th>
                          <th className="px-3 py-2 text-left">Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docsData.purchases.map((p: any) => (
                          <tr key={p.id} className="border-b">
                            <td className="px-3 py-2">{p.number || p.id.slice(0, 8)}</td>
                            <td className="px-3 py-2">{formatDate(p.date)}</td>
                            <td className="px-3 py-2">{p.supplier?.name || '—'}</td>
                            <td className="px-3 py-2 text-right">{p.totalAmount ?? 0}</td>
                            <td className="px-3 py-2">{p.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div>
                  <h3 className="font-medium mb-2">Расход ТМЦ ({docsData.consumptions.length})</h3>
                  {docsData.consumptions.length === 0 ? (
                    <div className="text-sm text-gray-500">Нет документов</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Дата</th>
                          <th className="px-3 py-2 text-left">Комментарий</th>
                          <th className="px-3 py-2 text-right">Позиций</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docsData.consumptions.map((c: any) => (
                          <tr key={c.id} className="border-b">
                            <td className="px-3 py-2">{formatDate(c.date)}</td>
                            <td className="px-3 py-2">{c.comment || '—'}</td>
                            <td className="px-3 py-2 text-right">{c.items?.length ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Warehouses
